import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, ArrowLeft, Gift, Check, X, Loader2, KeyRound, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MPIN Input component - 4 digit PIN
function MPINInput({ value, onChange, placeholder = "Enter 4-digit MPIN" }) {
  return (
    <Input
      type="password"
      inputMode="numeric"
      maxLength={4}
      pattern="[0-9]*"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
        onChange(val);
      }}
      className="text-center tracking-[0.5em] text-lg font-mono"
    />
  );
}

export default function PatientLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState('login'); // login, register, forgot
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Post-login redirect helper - checks for pending prescription barcode
  const navigateAfterLogin = () => {
    const pendingRx = localStorage.getItem('pending_rx_barcode');
    if (pendingRx) {
      localStorage.removeItem('pending_rx_barcode');
      navigate(`/patient/marketplace?rx=${pendingRx}`);
    } else {
      navigate('/patient/dashboard');
    }
  };
  
  // Login form
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [mpin, setMpin] = useState('');
  
  // Registration form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [newMpin, setNewMpin] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [bloodGroup, setBloodGroup] = useState('');
  const [address, setAddress] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  
  // Forgot MPIN
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotOTP, setForgotOTP] = useState('');
  const [resetMpin, setResetMpin] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [testOTP, setTestOTP] = useState('');
  
  // Google OAuth state
  const [googleData, setGoogleData] = useState(null);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('patient_token');
    if (token) {
      navigateAfterLogin();
    }
    
    // Check for referral code in URL
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      validateReferralCode(refCode);
    }
  }, [navigate, location]);

  // Handle Google OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        handleGoogleCallback(sessionId);
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [location]);

  const handleGoogleCallback = async (sessionId) => {
    setGoogleLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-auth/google-auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      
      const data = await res.json();
      
      if (data.is_new) {
        // New user - show registration form with Google data
        setGoogleData({
          email: data.google_email,
          name: data.google_name,
          picture: data.google_picture
        });
        setEmail(data.google_email);
        setName(data.google_name || '');
        setMode('register');
        toast.info('Please complete your registration');
      } else {
        // Existing user - login
        localStorage.setItem('patient_token', data.token);
        localStorage.setItem('patient_user', JSON.stringify(data.patient));
        toast.success('Welcome back!');
        navigateAfterLogin();
      }
    } catch (err) {
      toast.error('Google authentication failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/patient/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const validateReferralCode = async (code) => {
    if (!code) {
      setReferralValid(null);
      setReferrerName('');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/patient-auth/validate-referral/${code}`);
      if (res.ok) {
        const data = await res.json();
        setReferralValid(true);
        setReferrerName(data.referrer_name);
      } else {
        setReferralValid(false);
        setReferrerName('');
      }
    } catch {
      setReferralValid(false);
    }
  };

  const handleLogin = async () => {
    if (!emailOrPhone) {
      toast.error('Please enter your email or phone');
      return;
    }
    if (mpin.length !== 4) {
      toast.error('Please enter a valid 4-digit MPIN');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_or_phone: emailOrPhone,
          mpin: mpin
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('patient_token', data.token);
        localStorage.setItem('patient_user', JSON.stringify(data.patient));
        toast.success('Welcome back!');
        navigateAfterLogin();
      } else {
        const errorMessage = data.detail || '';
        
        if (errorMessage.toLowerCase().includes('no account found')) {
          toast.error('No account found with this email or phone.');
        } else if (errorMessage.toLowerCase().includes('incorrect mpin')) {
          toast.error('Incorrect MPIN. Please try again.');
        } else if (errorMessage.toLowerCase().includes('reset your mpin')) {
          toast.error('Please reset your MPIN using the "Forgot MPIN" option.');
        } else {
          toast.error(errorMessage || 'Login failed. Please check your credentials.');
        }
      }
    } catch (err) {
      toast.error('Network error. Please check your connection and try again.');
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    if (!name || !email || !phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (newMpin.length !== 4) {
      toast.error('Please set a 4-digit MPIN');
      return;
    }
    
    setLoading(true);
    try {
      const endpoint = googleData 
        ? `${API_URL}/api/patient-auth/google-register?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}&mpin=${newMpin}&phone=${encodeURIComponent(phone)}&date_of_birth=${dob || ''}&gender=${gender || ''}&blood_group=${bloodGroup || ''}&address=${encodeURIComponent(address)}&emergency_contact=${emergencyContact}&referral_code=${referralCode}`
        : `${API_URL}/api/patient-auth/register`;
      
      const options = googleData ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      } : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          email,
          mpin: newMpin,
          name,
          date_of_birth: dob || null,
          gender: gender || null,
          blood_group: bloodGroup || null,
          address,
          emergency_contact: emergencyContact,
          referral_code: referralCode
        })
      };
      
      const res = await fetch(endpoint, options);
      const data = await res.json();
      
      if (res.ok) {
        localStorage.setItem('patient_token', data.token);
        localStorage.setItem('patient_user', JSON.stringify(data.patient));
        toast.success('Registration successful! Welcome to 1Clin!');
        navigateAfterLogin();
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleForgotMPIN = async () => {
    if (!forgotIdentifier) {
      toast.error('Please enter your email or phone');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-auth/forgot-mpin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_or_phone: forgotIdentifier })
      });
      
      const data = await res.json();
      setOtpSent(true);
      setTestOTP(data.otp_for_testing || '');
      toast.success('OTP sent! Check your WhatsApp and email.');
    } catch (err) {
      toast.error('Failed to send OTP');
    }
    setLoading(false);
  };

  const handleResetMPIN = async () => {
    if (resetMpin.length !== 4) {
      toast.error('Please enter a 4-digit MPIN');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-auth/reset-mpin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_or_phone: forgotIdentifier,
          otp: forgotOTP,
          new_mpin: resetMpin
        })
      });
      
      if (res.ok) {
        toast.success('MPIN reset successfully! Please login.');
        setMode('login');
        setOtpSent(false);
        setForgotIdentifier('');
        setForgotOTP('');
        setResetMpin('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reset MPIN');
      }
    } catch (err) {
      toast.error('Network error');
    }
    setLoading(false);
  };

  if (googleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white">Completing Google Sign In...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md md:max-w-lg bg-white/95 backdrop-blur">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            {mode !== 'login' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setMode('login');
                  setGoogleData(null);
                  setOtpSent(false);
                }}
                className="absolute left-4"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
          </div>
          <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
            1Clin
          </div>
          <CardTitle className="text-xl mt-2">Patient Portal</CardTitle>
          <CardDescription>
            {mode === 'login' && 'Sign in to access your health records'}
            {mode === 'register' && 'Create your patient account'}
            {mode === 'forgot' && (otpSent ? 'Enter OTP and new MPIN' : 'Reset your MPIN')}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {mode === 'login' && (
            <>
              {/* Google Sign In Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full border-slate-300 hover:bg-slate-50"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or sign in with MPIN</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Email or Phone</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Email or phone number"
                    value={emailOrPhone}
                    onChange={(e) => setEmailOrPhone(e.target.value)}
                    className="pl-10"
                    data-testid="email-phone-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-slate-500" />
                  MPIN (4 digits)
                </label>
                <MPINInput value={mpin} onChange={setMpin} />
              </div>
              
              <Button
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={handleLogin}
                disabled={loading}
                data-testid="login-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Sign In
              </Button>
              
              <p className="text-xs text-center text-slate-500 bg-amber-50 border border-amber-200 rounded-md py-2 px-3" data-testid="forgot-mpin-hint">
                Use "Forgot MPIN?" if you are already registered at the clinic — do not create a new account.
              </p>

              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className="text-teal-600 hover:underline"
                >
                  Forgot MPIN?
                </button>
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className="text-teal-600 hover:underline"
                >
                  New patient? Register
                </button>
              </div>
            </>
          )}
          
          {mode === 'register' && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {googleData && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <p className="text-sm text-teal-800">
                    <Check className="w-4 h-4 inline mr-1" />
                    Signed in as <strong>{googleData.email}</strong>
                  </p>
                  <p className="text-xs text-teal-600 mt-1">Please complete your profile to continue</p>
                </div>
              )}

              {!googleData && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-slate-300 hover:bg-slate-50"
                    onClick={handleGoogleLogin}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign up with Google
                  </Button>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">Or register manually</span>
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Full Name *</label>
                <Input
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="name-input"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Email *</label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!googleData}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="pl-10"
                    data-testid="phone-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-slate-500" />
                  Set MPIN (4 digits) *
                </label>
                <MPINInput value={newMpin} onChange={setNewMpin} placeholder="Create your MPIN" />
                <p className="text-xs text-slate-500">This will be used to login to your account</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Date of Birth</label>
                  <Input
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Gender</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Blood Group</label>
                <Select value={bloodGroup} onValueChange={setBloodGroup}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Address</label>
                <Input
                  placeholder="Your address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Emergency Contact</label>
                <Input
                  placeholder="Emergency contact number"
                  value={emergencyContact}
                  onChange={(e) => setEmergencyContact(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Gift className="h-4 w-4 text-amber-500" /> Referral Code (Optional)
                </label>
                <div className="relative">
                  <Input
                    placeholder="Enter referral code"
                    value={referralCode}
                    onChange={(e) => {
                      setReferralCode(e.target.value.toUpperCase());
                      if (e.target.value.length >= 4) {
                        validateReferralCode(e.target.value);
                      } else {
                        setReferralValid(null);
                      }
                    }}
                    className="pr-10"
                  />
                  {referralValid === true && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
                  )}
                  {referralValid === false && (
                    <X className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
                  )}
                </div>
                {referralValid && referrerName && (
                  <p className="text-xs text-green-600">Referred by: {referrerName}</p>
                )}
              </div>
              
              <Button
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={handleRegister}
                disabled={loading || !name || !email || !phone || newMpin.length !== 4}
                data-testid="register-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Complete Registration
              </Button>
            </div>
          )}
          
          {mode === 'forgot' && (
            <>
              {!otpSent ? (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email or Phone</label>
                    <Input
                      type="text"
                      placeholder="Enter your registered email or phone"
                      value={forgotIdentifier}
                      onChange={(e) => setForgotIdentifier(e.target.value)}
                      data-testid="patient-forgot-identifier"
                    />
                    <p className="text-xs text-muted-foreground">OTP will be sent to your registered email & WhatsApp</p>
                  </div>
                  <Button
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={handleForgotMPIN}
                    disabled={loading || !forgotIdentifier}
                    data-testid="patient-forgot-send-otp"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Send OTP
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">OTP</label>
                    <Input
                      type="text"
                      placeholder="6-digit OTP"
                      value={forgotOTP}
                      onChange={(e) => setForgotOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center tracking-widest"
                      maxLength={6}
                    />
                    {testOTP && <p className="text-xs text-slate-500">Test OTP: {testOTP}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">New MPIN (4 digits)</label>
                    <MPINInput value={resetMpin} onChange={setResetMpin} placeholder="Enter new MPIN" />
                  </div>
                  <Button
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={handleResetMPIN}
                    disabled={loading || resetMpin.length !== 4}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Reset MPIN
                  </Button>
                </>
              )}
            </>
          )}
          
          <div className="text-center pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-muted-foreground"
            >
              Back to Portal Selection
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
